/**
 * Google Chat Setup Component
 * Allows admins to configure Google Chat OAuth for message polling
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Link,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { integrationsApi } from '@/services/api';

export default function GoogleChatSetup() {
  const queryClient = useQueryClient();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authUrl, setAuthUrl] = useState<string | null>(null);

  // Query OAuth status
  const { data: oauthStatus, isLoading: statusLoading, refetch: refetchStatus } = useQuery({
    queryKey: ['google-oauth-status'],
    queryFn: () => integrationsApi.googleAuthStatus().then((res) => res.data),
    refetchInterval: 30000,
  });

  // Query polling status
  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ['health-detailed'],
    queryFn: () => integrationsApi.getDetailedHealth().then((res) => res.data),
    refetchInterval: 10000,
  });

  // Mutation to initiate OAuth
  const [authError, setAuthError] = useState<string | null>(null);
  const initOAuthMutation = useMutation({
    mutationFn: () => {
      const callbackUrl = `${window.location.origin}/api/v1/integrations/google/auth/callback`;
      return integrationsApi.googleAuthInit(callbackUrl).then((res) => res.data);
    },
    onSuccess: (data) => {
      setAuthError(null);
      setAuthUrl(data.authorization_url);
      setAuthDialogOpen(true);
    },
    onError: (error: Error) => {
      console.error('OAuth init error:', error);
      setAuthError(error.message || 'Failed to initialize OAuth');
    },
  });

  const handleAuthorize = () => {
    initOAuthMutation.mutate();
  };

  const handleOpenAuthUrl = () => {
    if (authUrl) {
      window.open(authUrl, '_blank', 'width=600,height=700');
    }
  };

  const handleAuthComplete = () => {
    setAuthDialogOpen(false);
    setAuthUrl(null);
    setTimeout(() => {
      refetchStatus();
      queryClient.invalidateQueries({ queryKey: ['health-detailed'] });
    }, 2000);
  };

  const isLoading = statusLoading || healthLoading;
  const pollingStatus = healthData?.polling;

  return (
    <Box>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Status row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={
                oauthStatus?.configured
                  ? oauthStatus.valid
                    ? 'Connected'
                    : oauthStatus.error || 'Needs re-authorization'
                  : 'Not configured'
              }
              color={
                oauthStatus?.configured
                  ? oauthStatus.valid
                    ? 'success'
                    : 'warning'
                  : 'default'
              }
              size="small"
              variant="outlined"
            />
            {pollingStatus && (
              <>
                <Chip
                  label={pollingStatus.running ? 'Polling: Running' : 'Polling: Stopped'}
                  color={pollingStatus.running ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                />
                {pollingStatus.next_poll && (
                  <Typography variant="caption" color="text.secondary">
                    Next: {new Date(pollingStatus.next_poll).toLocaleTimeString()}
                  </Typography>
                )}
              </>
            )}
          </Box>

          {/* Error display */}
          {authError && (
            <Alert severity="error" onClose={() => setAuthError(null)}>
              {authError}
            </Alert>
          )}
          {oauthStatus?.configured && !oauthStatus.valid && oauthStatus.action_required && (
            <Alert severity="warning" variant="outlined">
              {oauthStatus.action_required}
            </Alert>
          )}

          {/* Actions row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant={oauthStatus?.valid ? 'outlined' : 'contained'}
              size="small"
              onClick={handleAuthorize}
              disabled={initOAuthMutation.isPending}
              startIcon={initOAuthMutation.isPending ? <CircularProgress size={14} /> : <ChatIcon />}
            >
              {oauthStatus?.valid ? 'Re-authorize' : 'Authorize Google Chat'}
            </Button>
            <Tooltip title="Refresh status">
              <IconButton size="small" onClick={() => refetchStatus()}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Authorization Dialog */}
      <Dialog open={authDialogOpen} onClose={() => setAuthDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Authorize Google Chat Access</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Click the button below to open Google's authorization page. Sign in with a Google account
            that has access to your team's Google Chat spaces.
          </DialogContentText>

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleOpenAuthUrl}
              endIcon={<OpenInNewIcon />}
              disabled={!authUrl}
            >
              Open Google Authorization
            </Button>
          </Box>

          {authUrl && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                If the popup is blocked, copy the URL:
              </Typography>
              <Link
                href={authUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', wordBreak: 'break-all' }}
              >
                {authUrl}
              </Link>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuthDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAuthComplete} variant="contained">
            I've Completed Authorization
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
